"use client"
import React, { useMemo } from "react";
import MapBoxMapWrapper from "@/components/shared/MapBoxMapWrapper";

import "@/components/maps/MapRouteDetailView.css";

const DEFAULT_LINE_COLOR = "#1565C0";
const DEFAULT_PIXEL_PADDING = 80;

const ensureColor = color => {
  if (!color) {
    return null;
  }
  const normalized = `${color}`.trim();
  if (!normalized) {
    return null;
  }
  return normalized.startsWith("#") ? normalized : `#${normalized}`;
};

const toNumber = value => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const MapRouteDetailView = ({ route, map }) => {
  if (!route || !route.directions) {
    return null;
  }

  const lineColor =
    ensureColor(
      route.feature?.properties?.hexColor ??
        route.hexColor ??
        route.feature?.properties?.color
    ) ?? DEFAULT_LINE_COLOR;

  const stopColor =
    ensureColor(
      route.feature?.properties?.stopHexColor ?? route.stopHexColor
    ) ?? lineColor;

  const coordinates = useMemo(() => {
    const coords = [];
    const push = (lng, lat) => {
      const normalizedLng = toNumber(lng);
      const normalizedLat = toNumber(lat);
      if (normalizedLng != null && normalizedLat != null) {
        coords.push([normalizedLng, normalizedLat]);
      }
    };

    const collect = value => {
      if (!value) {
        return;
      }
      if (Array.isArray(value)) {
        if (
          value.length >= 2 &&
          typeof value[0] === "number" &&
          typeof value[1] === "number"
        ) {
          push(value[0], value[1]);
        } else {
          value.forEach(collect);
        }
        return;
      }
      if (typeof value === "object" && value.coordinates) {
        collect(value.coordinates);
      }
    };

    collect(route.feature?.geometry);
    collect(route.geometry);

    const { directions } = route;
    if (directions?.origin) {
      push(directions.origin.longitude, directions.origin.latitude);
    }
    if (directions?.stops) {
      directions.stops.forEach(stop => {
        push(stop.longitude, stop.latitude);
      });
    }
    if (directions?.destination) {
      push(directions.destination.longitude, directions.destination.latitude);
    }

    return coords;
  }, [route]);

  const boundingBox = useMemo(() => {
    if (coordinates.length === 0) {
      return null;
    }

    let minLng = coordinates[0][0];
    let maxLng = coordinates[0][0];
    let minLat = coordinates[0][1];
    let maxLat = coordinates[0][1];

    coordinates.forEach(([lng, lat]) => {
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    });

    const minDelta = 0.0005;
    if (Math.abs(maxLng - minLng) < minDelta) {
      minLng -= minDelta;
      maxLng += minDelta;
    }
    if (Math.abs(maxLat - minLat) < minDelta) {
      minLat -= minDelta;
      maxLat += minDelta;
    }

    return [
      [minLng, minLat],
      [maxLng, maxLat]
    ];
  }, [coordinates]);

  const fallbackCenter = useMemo(() => {
    if (coordinates.length > 0) {
      const middle = coordinates[Math.floor(coordinates.length / 2)];
      return {
        longitude: middle[0],
        latitude: middle[1]
      };
    }
    const { directions } = route;
    const longitude = toNumber(directions?.origin?.longitude);
    const latitude = toNumber(directions?.origin?.latitude);
    if (longitude != null && latitude != null) {
      return { longitude, latitude };
    }
    return null;
  }, [coordinates, route]);

  const fixedSettings = useMemo(() => {
    if (!boundingBox) {
      return null;
    }
    return {
      boundingBox,
      pixelTolerance: DEFAULT_PIXEL_PADDING,
      defaultPitch: 0
    };
  }, [boundingBox]);

  const { origin, destination, stops = [] } = route.directions;

  const listStyle = {
    "--line-color": lineColor,
    "--circle-color": stopColor
  };

  const stopCount = stops.length;

  return (
    <div className="map-route-detail-view">
      <div className="map-route-detail-view__map">
        <MapBoxMapWrapper
          height="360px"
          width="100%"
          routes={[route]}
          selectedRoute={route}
          layers={map?.layers ?? []}
          locations={[]}
          mapToggle={{
            pins: false,
            regions: false,
            routes: true,
            waypoints: false,
            assetGroups: false,
            features: false
          }}
          fixedSettings={fixedSettings}
          addMyLocation={false}
          interactive={false}
          fullscreenEnabled={false}
          enableDraw={false}
          defaultCenter={fixedSettings ? null : fallbackCenter}
        />
      </div>

      <div className="map-route-detail-view__meta">
        {route.text && <p>{route.text}</p>}
        <div className="map-route-detail-view__summary">
          <div className="map-route-detail-view__stat">
            <span className="material-icons" aria-hidden="true">
              route
            </span>
            <span>&nbsp;{stopCount + 2} total stops</span>
          </div>
        </div>
      </div>

      <div className="map-route-detail-view__stops">
        <ul className="connected-line" style={listStyle}>
          {origin && <li className="stop">&nbsp;{origin.title}</li>}
          {stops.map((stop, index) => (
            <li key={`map-route-detail-stop-${index}`} className="stop">
              &nbsp;{stop.title}
            </li>
          ))}
          {destination && <li className="stop">&nbsp;{destination.title}</li>}
        </ul>
      </div>
    </div>
  );
};

export default MapRouteDetailView;
